import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  FileText,
  Plus,
  Truck,
  ArrowUpRight,
  ArrowDownLeft,
  Package,
  Printer,
  Trash2,
  RefreshCw,
  CheckCircle2,
  Clock,
  ChevronDown,
  Check,
  TrendingDown,
  TrendingUp,
  Eye,
  Upload,
  Layers,
  Gift,
} from "lucide-react";
import { toast } from "sonner";
import { useReactToPrint } from "react-to-print";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DeliveryChallan = () => {
  const [loading, setLoading] = useState(true);
  const [challans, setChallans] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [vendors, setVendors] = useState([]); // Dealers from database
  const [customers, setCustomers] = useState([]); // From work orders
  
  // Filter state
  const [vendorFilter, setVendorFilter] = useState("all");
  
  // Main dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [dcDirection, setDcDirection] = useState("outward"); // outward or inward
  const [dcType, setDcType] = useState("printing"); // printing, sample, corrugation
  
  // Common form fields
  const [dcForm, setDcForm] = useState({
    vendor_name: "",
    vehicle_no: "",
    remarks: "",
    from_address: "Challenge Packaging Industries\nSfno : 332/1A, Meetukuppam Main Road,\nVanagaram, Chennai - 600 095\nGSTIN - 33AGSPV7578C2ZP",
    to_address: "",
  });
  
  // Selected vendor object (to get address)
  const [selectedVendor, setSelectedVendor] = useState(null);
  
  // Printing specific
  const [printingForm, setPrintingForm] = useState({
    reel_id: "",
    paper_count: "",
  });
  const [selectedReel, setSelectedReel] = useState(null);
  const [reelSearchOpen, setReelSearchOpen] = useState(false);
  const [reelSearchTerm, setReelSearchTerm] = useState("");
  
  // Sample specific - multiple items support
  const [sampleItems, setSampleItems] = useState([
    { item_details: "", quantity: "", rate: "" }
  ]);
  const [sampleForm, setSampleForm] = useState({
    customer_name: "",
    item_details: "",
    quantity: "",
    rate: "",
    tax_rate: "18",
    custom_tax_rates: ["5", "12", "18"],
    new_tax_rate: "",
    signatory_attachment: null,
  });
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  
  // Corrugation specific - multiple reels support
  const [corrugationReels, setCorrugationReels] = useState([
    { reel_id: "", quantity: "1", weight: "", unit: "Nos", amount: "", selectedReel: null }
  ]);
  const [corrugationReelOpenIndex, setCorrugationReelOpenIndex] = useState(null);
  const [corrugationReelSearchTerms, setCorrugationReelSearchTerms] = useState({});
  
  // Vendor search
  const [vendorSearchOpen, setVendorSearchOpen] = useState(false);
  const [vendorSearchTerm, setVendorSearchTerm] = useState("");
  
  // Inward DC
  const [showInwardDialog, setShowInwardDialog] = useState(false);
  const [showInwardSelectDialog, setShowInwardSelectDialog] = useState(false);
  const [selectedOutwardDC, setSelectedOutwardDC] = useState(null);
  const [outwardDCSearchOpen, setOutwardDCSearchOpen] = useState(false);
  const [receivedEntries, setReceivedEntries] = useState([{ itemName: "", qty: "", rmName: "", paperType: "", totalRollWeight: "", remarks: "" }]);
  
  // Product Types from Store Database (for Inward DC Paper Type)
  const [productTypes, setProductTypes] = useState([]);
  
  // View DC dialog
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewDC, setViewDC] = useState(null);
  
  // Print refs
  const printRef = useRef(null);
  const lossReportRef = useRef(null);
  const [printDC, setPrintDC] = useState(null);
  
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [challansRes, inventoryRes, vendorsRes, workOrdersRes, productTypesRes, customersRes] = await Promise.all([
        axios.get(`${API}/delivery-challans`),
        axios.get(`${API}/inventory?status=available`),
        axios.get(`${API}/vendors`).catch(() => ({ data: [] })),
        axios.get(`${API}/work-orders`).catch(() => ({ data: [] })),
        axios.get(`${API}/product-types`).catch(() => ({ data: [] })),
        axios.get(`${API}/wo-customers`).catch(() => ({ data: [] })),
      ]);
      
      setChallans(challansRes.data || []);
      setInventory(inventoryRes.data || []);
      setProductTypes(productTypesRes.data || []);
      setVendors(vendorsRes.data || []);
      
      // Extract unique customer names from WO Database (wo_customers collection)
      const customerNames = [...new Set(
        (customersRes.data || [])
          .map(c => c.company_name)
          .filter(Boolean)
      )];
      setCustomers(customerNames);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Filter functions
  const filteredInventory = inventory.filter(item => {
    const searchLower = reelSearchTerm.toLowerCase();
    return (
      item.reel_id?.toLowerCase().includes(searchLower) ||
      item.rm_name?.toLowerCase().includes(searchLower) ||
      item.product?.toLowerCase().includes(searchLower)
    );
  });

  const filteredVendors = vendors.filter(v => 
    v.company_name?.toLowerCase().includes(vendorSearchTerm.toLowerCase()) ||
    v.vendor_name?.toLowerCase().includes(vendorSearchTerm.toLowerCase())
  );

  const filteredCustomers = customers.filter(c => 
    c?.toLowerCase().includes(customerSearchTerm.toLowerCase())
  );

  // Get pending outward DCs
  const pendingOutwardDCs = challans.filter(dc => 
    dc.dc_type === "outward" && dc.status === "created"
  );

  // Calculate sample total
  const calculateSampleTotal = () => {
    const qty = parseFloat(sampleForm.quantity) || 0;
    const rate = parseFloat(sampleForm.rate) || 0;
    const taxRate = parseFloat(sampleForm.tax_rate) || 0;
    const subtotal = qty * rate;
    const taxAmount = subtotal * (taxRate / 100);
    return {
      subtotal: subtotal.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      total: (subtotal + taxAmount).toFixed(2)
    };
  };

  // Add custom tax rate
  const addCustomTaxRate = () => {
    if (sampleForm.new_tax_rate && !sampleForm.custom_tax_rates.includes(sampleForm.new_tax_rate)) {
      setSampleForm(prev => ({
        ...prev,
        custom_tax_rates: [...prev.custom_tax_rates, prev.new_tax_rate],
        new_tax_rate: ""
      }));
    }
  };

  // Handle file upload for signatory
  const handleSignatoryUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSampleForm(prev => ({ ...prev, signatory_attachment: file }));
    }
  };

  // Create Outward DC
  const handleCreateOutwardDC = async () => {
    // For Sample DCs, customer_name is used instead of vendor_name
    if (dcType === "sample") {
      if (!sampleForm.customer_name) {
        toast.error("Please select a customer");
        return;
      }
    } else if (!dcForm.vendor_name) {
      toast.error("Please select a vendor/dealer");
      return;
    }

    let dcData = {
      dc_type: "outward",
      dc_subtype: dcType,
      vendor_name: dcType === "sample" ? sampleForm.customer_name : dcForm.vendor_name,
      vehicle_no: dcForm.vehicle_no,
      remarks: dcForm.remarks,
      items: [],
    };

    if (dcType === "printing") {
      if (!printingForm.reel_id || !printingForm.paper_count) {
        toast.error("Please select reel and enter paper count");
        return;
      }
      dcData.from_address = dcForm.from_address;
      dcData.to_address = dcForm.to_address;
      dcData.items = [{
        reel_id: printingForm.reel_id,
        rm_name: selectedReel?.rm_name || "",
        reel_size: selectedReel?.reel_size || null,
        qty: parseInt(printingForm.paper_count),
        unit: "Nos",
      }];
      dcData.sent_qty = parseInt(printingForm.paper_count);
    } else if (dcType === "sample") {
      // Handle multiple sample items
      const validItems = sampleItems.filter(it => it.item_details || it.quantity || it.rate);
      if (validItems.length === 0) {
        toast.error("Please add at least one item");
        return;
      }
      
      dcData.customer_name = sampleForm.customer_name;
      dcData.from_address = dcForm.from_address;
      dcData.to_address = dcForm.to_address;
      dcData.items = sampleItems.filter(it => it.item_details || it.quantity).map(item => ({
        description: item.item_details,
        qty: parseFloat(item.quantity) || 0,
        rate: parseFloat(item.rate) || 0,
        amount: (parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0),
        unit: "Nos",
      }));
      dcData.total_amount = sampleItems.reduce((sum, it) => 
        sum + ((parseFloat(it.quantity) || 0) * (parseFloat(it.rate) || 0)), 0
      );
      dcData.sent_qty = sampleItems.reduce((sum, it) => sum + (parseFloat(it.quantity) || 0), 0);
      // Handle signatory attachment (base64 encode for storage)
      if (sampleForm.signatory_attachment) {
        const reader = new FileReader();
        reader.readAsDataURL(sampleForm.signatory_attachment);
        reader.onload = () => {
          dcData.signatory_attachment = reader.result;
        };
      }
    } else if (dcType === "corrugation") {
      // Validate multiple reels
      const validReels = corrugationReels.filter(r => r.reel_id && r.quantity);
      if (validReels.length === 0) {
        toast.error("Please select at least one reel with quantity");
        return;
      }
      dcData.items = corrugationReels.filter(r => r.reel_id && r.quantity).map(reel => ({
        reel_id: reel.reel_id,
        rm_name: reel.selectedReel?.rm_name || "",
        reel_size: reel.selectedReel?.reel_size || null,
        weight: parseFloat(reel.weight) || reel.selectedReel?.reel_weight || 0,
        qty: parseFloat(reel.quantity),
        unit: reel.unit,
        amount: parseFloat(reel.amount) || 0,
      }));
      // Total qty is sum of all reel quantities
      dcData.sent_qty = corrugationReels.filter(r => r.reel_id && r.quantity).reduce((sum, r) => sum + parseFloat(r.quantity || 0), 0);
      dcData.sent_unit = corrugationReels[0]?.unit || "Nos";
    }

    try {
      const response = await axios.post(`${API}/delivery-challans`, dcData);
      toast.success(`DC ${response.data.dc_number} created successfully!`);
      setShowCreateDialog(false);
      resetForms();
      fetchData();
      setPrintDC(response.data);
    } catch (error) {
      console.error("Error creating DC:", error);
      toast.error("Failed to create Delivery Challan");
    }
  };

  // Open receive dialog for a specific DC
  const openReceiveDialog = (dc) => {
    setSelectedOutwardDC(dc);
    // Auto-populate one entry per DC item with item name pre-filled
    const items = dc.items || [];
    if (items.length > 0) {
      const entries = items.map(item => ({
        itemName: item.reel_id || item.rm_name || "",
        qty: "",
        rmName: "",
        paperType: "",
        totalRollWeight: "",
        remarks: "",
      }));
      setReceivedEntries(entries);
    } else {
      setReceivedEntries([{ itemName: "", qty: "", rmName: "", paperType: "", totalRollWeight: "", remarks: "" }]);
    }
    setShowInwardDialog(true);
  };

  const addReceivedEntry = () => {
    setReceivedEntries(prev => [...prev, { itemName: "", qty: "", rmName: "", paperType: "", totalRollWeight: "", remarks: "" }]);
  };

  const removeReceivedEntry = (idx) => {
    setReceivedEntries(prev => prev.filter((_, i) => i !== idx));
  };

  const updateReceivedEntry = (idx, field, value) => {
    setReceivedEntries(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  };

  // Handle Inward DC (Receive)
  const handleReceiveInward = async () => {
    const validEntries = receivedEntries.filter(e => e.qty && e.paperType);
    if (!selectedOutwardDC || validEntries.length === 0) {
      toast.error("Please enter paper type + quantity for at least one entry");
      return;
    }

    try {
      await axios.put(`${API}/delivery-challans/${selectedOutwardDC.dc_number}/receive`, {
        entries: validEntries.map(e => ({
          received_qty: parseFloat(e.qty),
          rm_name: e.rmName || "",
          item_name: e.itemName || "",
          remarks: e.remarks || "",
          paper_type: e.paperType || "",
          total_roll_weight: parseFloat(e.totalRollWeight) || 0,
        })),
        received_unit: selectedOutwardDC.sent_unit || "Nos",
        create_inventory: true,
      });
      
      toast.success("Received successfully! New stock IDs created in inventory.");
      setShowInwardDialog(false);
      setShowCreateDialog(false);
      setSelectedOutwardDC(null);
      setReceivedEntries([{ itemName: "", qty: "", rmName: "", paperType: "", totalRollWeight: "", remarks: "" }]);
      fetchData();
    } catch (error) {
      console.error("Error receiving:", error);
      toast.error("Failed to receive");
    }
  };

  // Delete DC
  const handleDeleteDC = async (dcNumber) => {
    if (!confirm(`Delete ${dcNumber}?`)) return;
    try {
      await axios.delete(`${API}/delivery-challans/${dcNumber}`);
      toast.success("Deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // Reset forms
  const resetForms = () => {
    setDcForm({ 
      vendor_name: "", 
      vehicle_no: "", 
      remarks: "",
      from_address: "Challenge Packaging Industries\nSfno : 332/1A, Meetukuppam Main Road,\nVanagaram, Chennai - 600 095\nGSTIN - 33AGSPV7578C2ZP",
      to_address: "",
    });
    setSelectedVendor(null);
    setPrintingForm({ reel_id: "", paper_count: "" });
    setSampleForm({
      customer_name: "", item_details: "",
      quantity: "", rate: "", tax_rate: "18", custom_tax_rates: ["5", "12", "18"],
      new_tax_rate: "", signatory_attachment: null,
    });
    // Reset sample items to single empty entry
    setSampleItems([{ item_details: "", quantity: "", rate: "" }]);
    // Reset corrugation reels to single empty entry
    setCorrugationReels([{ reel_id: "", quantity: "1", weight: "", unit: "Nos", amount: "", selectedReel: null }]);
    setCorrugationReelOpenIndex(null);
    setCorrugationReelSearchTerms({});
    setSelectedReel(null);
    setReelSearchTerm("");
    setVendorSearchTerm("");
    setCustomerSearchTerm("");
  };

  // Calculate loss
  const calculateLoss = (sent, received) => {
    const s = parseFloat(sent) || 0;
    const r = parseFloat(received) || 0;
    const loss = s - r;
    const pct = s > 0 ? ((loss / s) * 100).toFixed(1) : 0;
    return { loss, pct, isLoss: loss > 0 };
  };

  // Print DC handler - using callback for dialog content
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `DC_${printDC?.dc_number || 'document'}`,
    onAfterPrint: () => setPrintDC(null),
    onPrintError: (error) => {
      console.error("Print error:", error);
    }
  });

  // Print Loss Report handler
  const handlePrintLossReport = useReactToPrint({
    contentRef: lossReportRef,
    documentTitle: `Loss_Report_${vendorFilter === "all" ? "All_Vendors" : vendorFilter}`,
    onPrintError: (error) => {
      console.error("Print error:", error);
    }
  });

  // Filter challans
  const outwardChallans = challans.filter(dc => dc.dc_type === "outward");
  const printingChallans = outwardChallans.filter(dc => dc.dc_subtype === "printing");
  
  // Apply vendor filter
  const filteredChallans = vendorFilter === "all" 
    ? challans 
    : challans.filter(dc => dc.vendor_name === vendorFilter);
  
  // Get unique vendors from challans
  const uniqueVendors = [...new Set(challans.map(dc => dc.vendor_name).filter(Boolean))];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <RefreshCw className="h-12 w-12 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black text-white flex items-center gap-3">
              <FileText className="h-8 w-8 text-amber-400" />
              Delivery Challan Management
            </h1>
            <p className="text-gray-400 mt-1">Create and manage delivery challans</p>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold"
            data-testid="create-dc-btn"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create New DC
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-black/60 border border-blue-500/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Outward</p>
                <p className="text-2xl font-bold text-blue-400">{outwardChallans.length}</p>
              </div>
              <ArrowUpRight className="h-8 w-8 text-blue-400/50" />
            </CardContent>
          </Card>
          <Card className="bg-black/60 border border-purple-500/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Printing DCs</p>
                <p className="text-2xl font-bold text-purple-400">{printingChallans.length}</p>
              </div>
              <Printer className="h-8 w-8 text-purple-400/50" />
            </CardContent>
          </Card>
          <Card className="bg-black/60 border border-amber-500/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Pending Receipt</p>
                <p className="text-2xl font-bold text-amber-400">{pendingOutwardDCs.length}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-400/50" />
            </CardContent>
          </Card>
          <Card className="bg-black/60 border border-green-500/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Received</p>
                <p className="text-2xl font-bold text-green-400">
                  {challans.filter(dc => dc.status === "received").length}
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-400/50" />
            </CardContent>
          </Card>
        </div>

        {/* DC List Table */}
        <Card className="bg-black/60 border border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white">Delivery Challans</CardTitle>
            <div className="flex items-center gap-3">
              {/* Vendor Filter */}
              <div className="flex items-center gap-2">
                <Label className="text-gray-400 text-sm">Filter:</Label>
                <Select value={vendorFilter} onValueChange={setVendorFilter}>
                  <SelectTrigger className="w-[200px] bg-black/50 border-gray-600 text-white h-9">
                    <SelectValue placeholder="All Vendors" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    <SelectItem value="all">All Vendors</SelectItem>
                    {uniqueVendors.map(v => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Print Loss Report Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrintLossReport}
                className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Loss Report
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInwardSelectDialog(true)}
                disabled={pendingOutwardDCs.length === 0}
                className="border-green-500/50 text-green-400 hover:bg-green-500/10"
              >
                <ArrowDownLeft className="h-4 w-4 mr-2" />
                Receive Inward
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {filteredChallans.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-600" />
                <p>No delivery challans {vendorFilter !== "all" ? `for ${vendorFilter}` : "yet"}</p>
                <p className="text-sm mt-2">{vendorFilter !== "all" ? "Try a different filter" : "Click \"Create New DC\" to get started"}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-700">
                    <TableHead className="text-gray-400">DC Number</TableHead>
                    <TableHead className="text-gray-400">Type</TableHead>
                    <TableHead className="text-gray-400">Vendor</TableHead>
                    <TableHead className="text-gray-400">Sent Qty</TableHead>
                    <TableHead className="text-gray-400">Received</TableHead>
                    <TableHead className="text-gray-400">Loss Trend</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="text-gray-400">Date</TableHead>
                    <TableHead className="text-gray-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredChallans.map((dc) => {
                    const sentQty = dc.sent_qty || dc.items?.[0]?.qty || 0;
                    const receivedQty = dc.received_qty || 0;
                    const { loss, pct, isLoss } = calculateLoss(sentQty, receivedQty);
                    
                    return (
                      <TableRow key={dc.dc_number} className="border-gray-700 hover:bg-gray-800/50">
                        <TableCell className="font-bold text-cyan-400">{dc.dc_number}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            dc.dc_subtype === "printing" ? "border-purple-500/50 text-purple-400" :
                            dc.dc_subtype === "sample" ? "border-pink-500/50 text-pink-400" :
                            "border-orange-500/50 text-orange-400"
                          }>
                            {dc.dc_subtype === "printing" && <Printer className="h-3 w-3 mr-1" />}
                            {dc.dc_subtype === "sample" && <Gift className="h-3 w-3 mr-1" />}
                            {dc.dc_subtype === "corrugation" && <Layers className="h-3 w-3 mr-1" />}
                            {dc.dc_subtype || "N/A"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-300">{dc.vendor_name}</TableCell>
                        <TableCell className="text-blue-400 font-medium">
                          {sentQty} {dc.sent_unit || "Nos"}
                        </TableCell>
                        <TableCell>
                          {dc.status === "received" ? (
                            <span className="text-green-400 font-medium">{receivedQty}</span>
                          ) : (
                            <span className="text-gray-500">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {dc.status === "received" && dc.dc_subtype === "printing" ? (
                            <div className="flex items-center gap-2">
                              {isLoss ? (
                                <>
                                  <TrendingDown className="h-4 w-4 text-red-400" />
                                  <span className="text-red-400 text-sm">-{loss} ({pct}%)</span>
                                </>
                              ) : loss < 0 ? (
                                <>
                                  <TrendingUp className="h-4 w-4 text-green-400" />
                                  <span className="text-green-400 text-sm">+{Math.abs(loss)}</span>
                                </>
                              ) : (
                                <span className="text-gray-400 text-sm">No loss</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-500">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            dc.status === "received" 
                              ? "bg-green-500/20 text-green-400 border-green-500/30"
                              : "bg-amber-500/20 text-amber-400 border-amber-500/30"
                          }>
                            {dc.status === "received" ? "Received" : "Pending"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-400 text-sm">
                          {new Date(dc.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-cyan-400 hover:bg-cyan-500/10"
                              onClick={() => { setViewDC(dc); setShowViewDialog(true); }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-blue-400 hover:bg-blue-500/10"
                              onClick={() => setPrintDC(dc)}
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-400 hover:bg-red-500/10"
                              onClick={() => handleDeleteDC(dc.dc_number)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            {dc.status === "pending" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-green-400 hover:bg-green-500/10"
                                onClick={() => openReceiveDialog(dc)}
                                data-testid={`receive-btn-${dc.dc_number}`}
                              >
                                <ArrowDownLeft className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create New DC Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-amber-400 text-xl flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Create New Delivery Challan
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Direction & Type Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400">Direction</Label>
                <Select value={dcDirection} onValueChange={setDcDirection}>
                  <SelectTrigger className="bg-black/50 border-gray-600 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    <SelectItem value="outward">
                      <span className="flex items-center gap-2">
                        <ArrowUpRight className="h-4 w-4 text-blue-400" />
                        Outward
                      </span>
                    </SelectItem>
                    <SelectItem value="inward">
                      <span className="flex items-center gap-2">
                        <ArrowDownLeft className="h-4 w-4 text-green-400" />
                        Inward
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {dcDirection === "outward" && (
                <div>
                  <Label className="text-gray-400">Type</Label>
                  <Select value={dcType} onValueChange={setDcType}>
                    <SelectTrigger className="bg-black/50 border-gray-600 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-700">
                      <SelectItem value="printing">
                        <span className="flex items-center gap-2">
                          <Printer className="h-4 w-4 text-purple-400" />
                          Printing
                        </span>
                      </SelectItem>
                      <SelectItem value="sample">
                        <span className="flex items-center gap-2">
                          <Gift className="h-4 w-4 text-pink-400" />
                          Sample
                        </span>
                      </SelectItem>
                      <SelectItem value="corrugation">
                        <span className="flex items-center gap-2">
                          <Layers className="h-4 w-4 text-orange-400" />
                          Corrugation
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Outward Forms */}
            {dcDirection === "outward" && (
              <>
                {/* Common: Vendor Selection - Only for printing and corrugation types */}
                {(dcType === "printing" || dcType === "corrugation") && (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg space-y-4">
                    <p className="text-amber-400 font-medium">Vendor/Dealer Details</p>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-400">Vendor/Dealer Name *</Label>
                        <Popover open={vendorSearchOpen} onOpenChange={setVendorSearchOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-between bg-black/50 border-gray-600 text-white mt-1"
                            >
                              {dcForm.vendor_name || "Search vendor..."}
                              <ChevronDown className="h-4 w-4 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[400px] p-0 bg-gray-900 border-gray-700">
                            <Command className="bg-gray-900" shouldFilter={false}>
                              <CommandInput 
                                placeholder="Search vendor..." 
                                value={vendorSearchTerm}
                                onValueChange={setVendorSearchTerm}
                              />
                              <CommandList className="max-h-48">
                                <CommandEmpty>No vendors found</CommandEmpty>
                                <CommandGroup>
                                  {filteredVendors.map(v => (
                                    <CommandItem
                                      key={v.id}
                                      onSelect={() => {
                                        setSelectedVendor(v);
                                        setDcForm(prev => ({ 
                                          ...prev, 
                                          vendor_name: v.company_name,
                                          to_address: v.address || ""
                                        }));
                                        setVendorSearchOpen(false);
                                      }}
                                      className="text-white hover:bg-amber-500/20 cursor-pointer"
                                    >
                                      <Check className={cn("mr-2 h-4 w-4", dcForm.vendor_name === v.company_name ? "opacity-100" : "opacity-0")} />
                                      <div className="flex flex-col">
                                        <span className="font-medium">{v.company_name}</span>
                                        <span className="text-xs text-gray-400">{v.vendor_name}</span>
                                      </div>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                      
                      <div>
                        <Label className="text-gray-400">Vehicle No.</Label>
                        <Input
                          value={dcForm.vehicle_no}
                          onChange={(e) => setDcForm({ ...dcForm, vehicle_no: e.target.value })}
                          placeholder="TN01AB1234"
                          className="bg-black/50 border-gray-600 text-white mt-1"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-gray-400">Remarks</Label>
                      <Textarea
                        value={dcForm.remarks}
                        onChange={(e) => setDcForm({ ...dcForm, remarks: e.target.value })}
                        placeholder="Any remarks..."
                        className="bg-black/50 border-gray-600 text-white mt-1"
                        rows={2}
                      />
                    </div>
                  </div>
                )}

                {/* PRINTING Form */}
                {dcType === "printing" && (
                  <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg space-y-4">
                    <p className="text-purple-400 font-medium flex items-center gap-2">
                      <Printer className="h-4 w-4" /> Printing Details
                    </p>
                    
                    {/* From and To Address */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-400">From Address</Label>
                        <Textarea
                          value={dcForm.from_address}
                          onChange={(e) => setDcForm({ ...dcForm, from_address: e.target.value })}
                          placeholder="Sender's address..."
                          className="bg-black/50 border-purple-500/30 text-white mt-1 text-sm"
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label className="text-gray-400">To Address {selectedVendor && <span className="text-green-400 text-xs">(Auto-filled)</span>}</Label>
                        <Textarea
                          value={dcForm.to_address}
                          onChange={(e) => setDcForm({ ...dcForm, to_address: e.target.value })}
                          placeholder="Vendor's address..."
                          className="bg-black/50 border-purple-500/30 text-white mt-1 text-sm"
                          rows={3}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-gray-400">Reel ID *</Label>
                      <Popover open={reelSearchOpen} onOpenChange={setReelSearchOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-between bg-black/50 border-purple-500/30 text-white mt-1"
                          >
                            {selectedReel ? (
                              <span><span className="text-cyan-400 font-bold">{selectedReel.reel_id}</span> - {selectedReel.rm_name}</span>
                            ) : "Search reel..."}
                            <ChevronDown className="h-4 w-4 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[500px] p-0 bg-gray-900 border-purple-500/30">
                          <Command className="bg-gray-900" shouldFilter={false}>
                            <CommandInput 
                              placeholder="Search reel ID, RM name..." 
                              value={reelSearchTerm}
                              onValueChange={setReelSearchTerm}
                            />
                            <CommandList className="max-h-60">
                              <CommandEmpty>No reels found</CommandEmpty>
                              <CommandGroup>
                                {filteredInventory.map(item => (
                                  <CommandItem
                                    key={item.id}
                                    onSelect={() => {
                                      setSelectedReel(item);
                                      setPrintingForm(prev => ({ ...prev, reel_id: item.reel_id }));
                                      setReelSearchOpen(false);
                                    }}
                                    className="text-white hover:bg-purple-500/20 cursor-pointer"
                                  >
                                    <Check className={cn("mr-2 h-4 w-4", selectedReel?.reel_id === item.reel_id ? "opacity-100" : "opacity-0")} />
                                    <div className="flex flex-col">
                                      <span className="text-cyan-400 font-bold">{item.reel_id}</span>
                                      <span className="text-xs text-gray-400">{item.rm_name} | {item.reel_size}" | {item.reel_weight} Kg</span>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    
                    {selectedReel && (
                      <div className="p-2 bg-cyan-500/10 rounded text-xs text-gray-300">
                        <span className="text-cyan-400">Selected:</span> {selectedReel.rm_name} | Size: {selectedReel.reel_size}" | Weight: {selectedReel.reel_weight} Kg
                      </div>
                    )}
                    
                    <div>
                      <Label className="text-gray-400">Paper Count (Nos) *</Label>
                      <Input
                        type="number"
                        value={printingForm.paper_count}
                        onChange={(e) => setPrintingForm({ ...printingForm, paper_count: e.target.value })}
                        placeholder="Number of papers"
                        className="bg-black/50 border-purple-500/30 text-white mt-1"
                      />
                    </div>
                  </div>
                )}

                {/* SAMPLE Form */}
                {dcType === "sample" && (
                  <div className="p-4 bg-pink-500/10 border border-pink-500/30 rounded-lg space-y-4">
                    <p className="text-pink-400 font-medium flex items-center gap-2">
                      <Gift className="h-4 w-4" /> Sample Details
                    </p>
                    
                    <div>
                      <Label className="text-gray-400">Customer Name *</Label>
                      <Popover open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-between bg-black/50 border-gray-600 text-white mt-1" data-testid="sample-customer-select">
                            {sampleForm.customer_name || "Search customer from WO Database..."}
                            <ChevronDown className="h-4 w-4 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0 bg-gray-900 border-gray-700">
                          <Command className="bg-gray-900" shouldFilter={false}>
                            <CommandInput 
                              placeholder="Search customer..." 
                              value={customerSearchTerm}
                              onValueChange={setCustomerSearchTerm}
                            />
                            <CommandList className="max-h-48">
                              <CommandEmpty>No customers found in WO Database</CommandEmpty>
                              <CommandGroup>
                                {filteredCustomers.map((c, i) => (
                                  <CommandItem
                                    key={i}
                                    onSelect={() => {
                                      setSampleForm(prev => ({ ...prev, customer_name: c }));
                                      setCustomerSearchOpen(false);
                                    }}
                                    className="text-white hover:bg-pink-500/20 cursor-pointer"
                                  >
                                    {c}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    
                    {/* From and To Address for Sample */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-400">From Address</Label>
                        <Textarea
                          value={dcForm.from_address}
                          onChange={(e) => setDcForm({ ...dcForm, from_address: e.target.value })}
                          placeholder="Sender's address..."
                          className="bg-black/50 border-pink-500/30 text-white mt-1 text-sm"
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label className="text-gray-400">To Address / Customer Address</Label>
                        <Textarea
                          value={dcForm.to_address}
                          onChange={(e) => setDcForm({ ...dcForm, to_address: e.target.value })}
                          placeholder="Customer/Vendor address..."
                          className="bg-black/50 border-pink-500/30 text-white mt-1 text-sm"
                          rows={3}
                        />
                      </div>
                    </div>
                    
                    {/* Multiple Items Section */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-gray-400">Items</Label>
                        <Button
                          size="sm"
                          onClick={() => setSampleItems(prev => [...prev, { item_details: "", quantity: "", rate: "" }])}
                          className="bg-pink-500 hover:bg-pink-600 text-black h-7 px-2"
                        >
                          <Plus className="h-3 w-3 mr-1" /> Add Item
                        </Button>
                      </div>
                      
                      {sampleItems.map((item, index) => (
                        <div key={index} className="p-3 bg-black/30 rounded-lg border border-pink-500/20 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-pink-300 text-sm font-medium">Item #{index + 1}</span>
                            {sampleItems.length > 1 && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setSampleItems(prev => prev.filter((_, i) => i !== index))}
                                className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                          
                          <div>
                            <Label className="text-gray-400 text-xs">Description</Label>
                            <Textarea
                              value={item.item_details}
                              onChange={(e) => setSampleItems(prev => prev.map((it, i) => 
                                i === index ? { ...it, item_details: e.target.value } : it
                              ))}
                              placeholder="Describe the item..."
                              className="bg-black/50 border-gray-600 text-white mt-1 text-sm"
                              rows={1}
                            />
                          </div>
                          
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <Label className="text-gray-400 text-xs">Quantity</Label>
                              <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => setSampleItems(prev => prev.map((it, i) => 
                                  i === index ? { ...it, quantity: e.target.value } : it
                                ))}
                                className="bg-black/50 border-gray-600 text-white mt-1 h-8 text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-gray-400 text-xs">Rate (₹)</Label>
                              <Input
                                type="number"
                                value={item.rate}
                                onChange={(e) => setSampleItems(prev => prev.map((it, i) => 
                                  i === index ? { ...it, rate: e.target.value } : it
                                ))}
                                className="bg-black/50 border-gray-600 text-white mt-1 h-8 text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-gray-400 text-xs">Amount (₹)</Label>
                              <Input
                                value={(parseFloat(item.quantity || 0) * parseFloat(item.rate || 0)).toFixed(2)}
                                readOnly
                                className="bg-black/50 border-gray-600 text-green-400 mt-1 h-8 text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {/* Total Amount */}
                      <div className="p-2 bg-green-500/10 border border-green-500/30 rounded flex justify-between items-center">
                        <span className="text-gray-400 text-sm">Total:</span>
                        <span className="text-xl font-bold text-green-400">
                          ₹{sampleItems.reduce((sum, it) => sum + (parseFloat(it.quantity || 0) * parseFloat(it.rate || 0)), 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Signatory Attachment */}
                    <div>
                      <Label className="text-gray-400">Authorised Signatory (Attachment)</Label>
                      <div className="mt-1 flex items-center gap-2">
                        <Input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={handleSignatoryUpload}
                          className="bg-black/50 border-gray-600 text-white"
                        />
                        {sampleForm.signatory_attachment && (
                          <Badge className="bg-green-500/20 text-green-400">
                            {sampleForm.signatory_attachment.name}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* CORRUGATION Form */}
                {dcType === "corrugation" && (
                  <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-orange-400 font-medium flex items-center gap-2">
                        <Layers className="h-4 w-4" /> Corrugation Details
                      </p>
                      <Button
                        size="sm"
                        onClick={() => {
                          setCorrugationReels(prev => [...prev, { reel_id: "", quantity: "1", weight: "", unit: "Nos", amount: "" }]);
                        }}
                        className="bg-orange-500 hover:bg-orange-600 text-black h-7 px-2"
                      >
                        <Plus className="h-3 w-3 mr-1" /> Add Reel
                      </Button>
                    </div>
                    
                    {/* From and To Address for Corrugation */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-400">From Address</Label>
                        <Textarea
                          value={dcForm.from_address}
                          onChange={(e) => setDcForm({ ...dcForm, from_address: e.target.value })}
                          placeholder="Sender's address..."
                          className="bg-black/50 border-orange-500/30 text-white mt-1 text-sm"
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label className="text-gray-400">To Address / Vendor Address</Label>
                        <Textarea
                          value={dcForm.to_address}
                          onChange={(e) => setDcForm({ ...dcForm, to_address: e.target.value })}
                          placeholder="Vendor address..."
                          className="bg-black/50 border-orange-500/30 text-white mt-1 text-sm"
                          rows={3}
                        />
                      </div>
                    </div>
                    
                    {corrugationReels.map((reel, index) => (
                      <div key={index} className="p-3 bg-black/30 rounded-lg border border-orange-500/20 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-orange-300 text-sm font-medium">Reel #{index + 1}</span>
                          {corrugationReels.length > 1 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setCorrugationReels(prev => prev.filter((_, i) => i !== index))}
                              className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        
                        <div>
                          <Label className="text-gray-400">Reel ID *</Label>
                          <Popover 
                            open={corrugationReelOpenIndex === index} 
                            onOpenChange={(open) => setCorrugationReelOpenIndex(open ? index : null)}
                          >
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-full justify-between bg-black/50 border-orange-500/30 text-white mt-1"
                              >
                                {reel.selectedReel ? (
                                  <span><span className="text-cyan-400 font-bold">{reel.selectedReel.reel_id}</span> - {reel.selectedReel.rm_name}</span>
                                ) : "Search reel..."}
                                <ChevronDown className="h-4 w-4 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[500px] p-0 bg-gray-900 border-orange-500/30">
                              <Command className="bg-gray-900" shouldFilter={false}>
                                <CommandInput 
                                  placeholder="Search reel ID, RM name..." 
                                  value={corrugationReelSearchTerms[index] || ""}
                                  onValueChange={(val) => setCorrugationReelSearchTerms(prev => ({ ...prev, [index]: val }))}
                                />
                                <CommandList className="max-h-60">
                                  <CommandEmpty>No reels found</CommandEmpty>
                                  <CommandGroup>
                                    {inventory.filter(item => {
                                      const search = (corrugationReelSearchTerms[index] || "").toLowerCase();
                                      return (
                                        item.reel_id?.toLowerCase().includes(search) ||
                                        item.rm_name?.toLowerCase().includes(search) ||
                                        item.product?.toLowerCase().includes(search)
                                      );
                                    }).map(item => (
                                      <CommandItem
                                        key={item.id}
                                        onSelect={() => {
                                          setCorrugationReels(prev => prev.map((r, i) => 
                                            i === index 
                                              ? { ...r, reel_id: item.reel_id, weight: item.reel_weight?.toString() || "", quantity: "1", selectedReel: item }
                                              : r
                                          ));
                                          setCorrugationReelOpenIndex(null);
                                        }}
                                        className="text-white hover:bg-orange-500/20 cursor-pointer"
                                      >
                                        <Check className={cn("mr-2 h-4 w-4", reel.reel_id === item.reel_id ? "opacity-100" : "opacity-0")} />
                                        <div className="flex flex-col">
                                          <span className="text-cyan-400 font-bold">{item.reel_id}</span>
                                          <span className="text-xs text-gray-400">{item.rm_name} | {item.reel_size}" | {item.reel_weight} Kg</span>
                                        </div>
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                        
                        <div className="grid grid-cols-4 gap-3">
                          <div>
                            <Label className="text-gray-400">Weight (Kg)</Label>
                            <Input
                              type="number"
                              value={reel.weight}
                              readOnly
                              className="bg-black/50 border-orange-500/30 text-cyan-400 mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-gray-400">Quantity *</Label>
                            <Input
                              type="number"
                              value={reel.quantity}
                              onChange={(e) => setCorrugationReels(prev => prev.map((r, i) => 
                                i === index ? { ...r, quantity: e.target.value } : r
                              ))}
                              className="bg-black/50 border-orange-500/30 text-white mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-gray-400">Unit</Label>
                            <Select 
                              value={reel.unit} 
                              onValueChange={(v) => setCorrugationReels(prev => prev.map((r, i) => 
                                i === index ? { ...r, unit: v } : r
                              ))}
                            >
                              <SelectTrigger className="bg-black/50 border-gray-600 text-white mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-gray-900 border-gray-700">
                                <SelectItem value="Nos">Nos</SelectItem>
                                <SelectItem value="Kgs">Kgs</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-gray-400">Amount (₹)</Label>
                            <Input
                              type="number"
                              value={reel.amount}
                              onChange={(e) => setCorrugationReels(prev => prev.map((r, i) => 
                                i === index ? { ...r, amount: e.target.value } : r
                              ))}
                              className="bg-black/50 border-orange-500/30 text-white mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Inward - Select Outward DC and Enter Received Data */}
            {dcDirection === "inward" && (
              <div className="space-y-4">
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg space-y-4">
                  <p className="text-green-400 font-medium">Select Sent Outward DC</p>
                  
                  {!selectedOutwardDC ? (
                    <>
                      {pendingOutwardDCs.length === 0 ? (
                        <div className="text-center py-8 text-gray-400">
                          <Clock className="h-8 w-8 mx-auto mb-2 text-gray-600" />
                          <p>No pending outward DCs</p>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {pendingOutwardDCs.map(dc => (
                            <div
                              key={dc.dc_number}
                              onClick={() => {
                                setSelectedOutwardDC(dc);
                                const defaultRm = dc.items?.[0]?.rm_name || dc.items?.[0]?.reel_id || "";
                                setReceivedEntries([{ qty: "", rmName: defaultRm, remarks: "", paperType: "" }]);
                              }}
                              className="p-3 bg-black/30 border border-gray-700 rounded cursor-pointer hover:border-green-500/50 transition-all"
                              data-testid={`inward-dc-select-${dc.dc_number}`}
                            >
                              <div className="flex justify-between items-center">
                                <span className="font-bold text-cyan-400">{dc.dc_number}</span>
                                <Badge variant="outline" className={
                                  dc.dc_subtype === "printing" ? "border-purple-500/50 text-purple-400" :
                                  dc.dc_subtype === "corrugation" ? "border-orange-500/50 text-orange-400" :
                                  "border-gray-500/50 text-gray-400"
                                }>
                                  {dc.dc_subtype}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-400 mt-1">
                                {dc.vendor_name} | Sent: {dc.sent_qty} {dc.sent_unit || "Nos"}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Selected DC Summary */}
                      <div className="p-3 bg-black/30 border border-green-500/30 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-bold text-cyan-400">{selectedOutwardDC.dc_number}</span>
                          <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white" onClick={() => setSelectedOutwardDC(null)}>
                            Change DC
                          </Button>
                        </div>
                        <p className="text-sm text-gray-400">
                          {selectedOutwardDC.vendor_name} | Type: {selectedOutwardDC.dc_subtype} | Sent: {selectedOutwardDC.sent_qty} {selectedOutwardDC.sent_unit || "Nos"}
                        </p>
                        {selectedOutwardDC.items?.map((item, i) => (
                          <p key={i} className="text-xs text-gray-500 mt-1">
                            Reel: {item.reel_id || item.rm_name} - {item.paper_count || item.quantity} {selectedOutwardDC.sent_unit || "Nos"}
                          </p>
                        ))}
                      </div>

                      {/* Enter Received Data - Multiple Entries */}
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-green-400 font-medium">Received Entries</p>
                        <Button size="sm" variant="outline" className="border-green-500/30 text-green-400 hover:bg-green-500/10" onClick={addReceivedEntry} data-testid="inward-add-entry">
                          + Add Entry
                        </Button>
                      </div>
                      
                      {receivedEntries.map((entry, idx) => (
                        <div key={idx} className="p-3 bg-black/30 border border-gray-700 rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-400 font-semibold">Entry #{idx + 1}</span>
                            {receivedEntries.length > 1 && (
                              <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10 h-6 px-2 text-xs" onClick={() => removeReceivedEntry(idx)}>Remove</Button>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <Label className="text-gray-400 text-xs">Paper Type *</Label>
                              <Select
                                value={entry.paperType || ""}
                                onValueChange={(value) => updateReceivedEntry(idx, "paperType", value)}
                              >
                                <SelectTrigger className="bg-black/50 border-green-500/30 text-white mt-1" data-testid={`inward-paper-type-${idx}`}>
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent className="bg-gray-900 border-green-500">
                                  {productTypes.length > 0
                                    ? productTypes.map(pt => (
                                        <SelectItem key={pt.id} value={pt.name} className="text-white hover:bg-green-500/20">
                                          {pt.name}
                                        </SelectItem>
                                      ))
                                    : ['Plain Kraft', 'Plain Duplex', 'Plain Gyt', 'Plain Imported', 'Printed Kraft', 'Printed Duplex', 'Printed Gyt', 'Printed Imported'].map(type => (
                                        <SelectItem key={type} value={type} className="text-white hover:bg-green-500/20">
                                          {type}
                                        </SelectItem>
                                      ))
                                  }
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-gray-400 text-xs">Received Qty ({selectedOutwardDC.sent_unit || "Nos"}) *</Label>
                              <Input type="number" value={entry.qty} onChange={(e) => updateReceivedEntry(idx, "qty", e.target.value)} placeholder="Quantity" className="bg-black/50 border-green-500/30 text-white mt-1" data-testid={`inward-qty-${idx}`} />
                            </div>
                            <div>
                              <Label className="text-gray-400 text-xs">RM Name</Label>
                              <Input value={entry.rmName} onChange={(e) => updateReceivedEntry(idx, "rmName", e.target.value)} placeholder="RM Name" className="bg-black/50 border-green-500/30 text-white mt-1" data-testid={`inward-rm-${idx}`} />
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {receivedEntries.some(e => e.qty) && (
                        <div className="p-3 bg-black/30 rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 text-sm">Total Received:</span>
                            <span className="text-white font-bold">{receivedEntries.reduce((s, e) => s + (parseFloat(e.qty) || 0), 0)} {selectedOutwardDC.sent_unit || "Nos"}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 text-sm">Loss/Gain:</span>
                            {(() => {
                              const sent = parseFloat(selectedOutwardDC.sent_qty) || 0;
                              const totalRcv = receivedEntries.reduce((s, e) => s + (parseFloat(e.qty) || 0), 0);
                              const diff = sent - totalRcv;
                              return <span className={`font-bold ${diff > 0 ? "text-red-400" : diff < 0 ? "text-green-400" : "text-gray-400"}`}>
                                {diff.toFixed(0)} ({(diff / sent * 100).toFixed(1)}%)
                              </span>;
                            })()}
                          </div>
                        </div>
                      )}

                      <Button
                        onClick={handleReceiveInward}
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                        disabled={!receivedEntries.some(e => e.qty)}
                        data-testid="inward-submit-btn"
                      >
                        <ArrowDownLeft className="h-4 w-4 mr-2" />
                        Receive Inward
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForms(); }} className="border-gray-500 text-gray-300">
              Cancel
            </Button>
            {dcDirection === "outward" && (
              <Button onClick={handleCreateOutwardDC} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                <Plus className="h-4 w-4 mr-2" />
                Create DC
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inward Receive Dialog */}
      <Dialog open={showInwardDialog} onOpenChange={setShowInwardDialog}>
        <DialogContent className="bg-gray-900 border-green-500/50 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-green-400 text-xl flex items-center gap-2">
              <ArrowDownLeft className="h-5 w-5" />
              Receive Inward - {selectedOutwardDC?.dc_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOutwardDC && (
            <div className="space-y-6 py-4">
              {/* View Sent Data */}
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-blue-400 font-medium mb-3">Sent Details (View)</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400">DC Number</p>
                    <p className="text-white font-bold">{selectedOutwardDC.dc_number}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Type</p>
                    <p className="text-white font-bold capitalize">{selectedOutwardDC.dc_subtype}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Vendor</p>
                    <p className="text-white font-bold">{selectedOutwardDC.vendor_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Vehicle No</p>
                    <p className="text-white">{selectedOutwardDC.vehicle_no || "N/A"}</p>
                  </div>
                  {selectedOutwardDC.items?.[0]?.reel_id && (
                    <div>
                      <p className="text-gray-400">Reel ID</p>
                      <p className="text-cyan-400 font-bold">{selectedOutwardDC.items[0].reel_id}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-400">Sent Quantity</p>
                    <p className="text-blue-400 font-bold text-xl">{selectedOutwardDC.sent_qty} {selectedOutwardDC.sent_unit || "Nos"}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Date</p>
                    <p className="text-white">{new Date(selectedOutwardDC.created_at).toLocaleDateString()}</p>
                  </div>
                  {selectedOutwardDC.remarks && (
                    <div className="col-span-2">
                      <p className="text-gray-400">Remarks</p>
                      <p className="text-white">{selectedOutwardDC.remarks}</p>
                    </div>
                  )}
                </div>
                
                {/* Show items details for corrugation DCs */}
                {selectedOutwardDC.dc_subtype === "corrugation" && selectedOutwardDC.items?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-blue-500/30">
                    <p className="text-blue-400 font-medium mb-2">Items Sent:</p>
                    <div className="space-y-2">
                      {selectedOutwardDC.items.map((item, idx) => (
                        <div key={idx} className="p-2 bg-black/30 rounded flex justify-between items-center">
                          <div>
                            <span className="text-cyan-400 font-mono">{item.reel_id}</span>
                            {item.rm_name && <span className="text-gray-400 ml-2">| {item.rm_name}</span>}
                          </div>
                          <div className="text-right">
                            {item.weight > 0 && <span className="text-orange-400 font-bold">{item.weight} Kg</span>}
                            <span className="text-gray-400 ml-2">Qty: {item.qty} {item.unit}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Enter Received Qty - Auto-populated from DC items */}
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-green-400 font-medium">Enter Received Data</p>
                  <Button size="sm" variant="outline" className="border-green-500/30 text-green-400 hover:bg-green-500/10" onClick={addReceivedEntry} data-testid="add-entry-btn-dialog">
                    + Add Entry
                  </Button>
                </div>
                
                {receivedEntries.map((entry, idx) => (
                  <div key={idx} className="p-3 bg-black/30 border border-gray-700 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400 font-semibold">Entry #{idx + 1}</span>
                      {receivedEntries.length > 1 && (
                        <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10 h-6 px-2 text-xs" onClick={() => removeReceivedEntry(idx)}>Remove</Button>
                      )}
                    </div>
                    
                    {/* Printing DC: Simple single-row layout */}
                    {selectedOutwardDC.dc_subtype === "printing" && (
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-gray-400 text-xs">Paper Type *</Label>
                          <Select
                            value={entry.paperType || ""}
                            onValueChange={(value) => updateReceivedEntry(idx, "paperType", value)}
                          >
                            <SelectTrigger className="bg-black/50 border-green-500/30 text-white mt-1" data-testid={`dialog-paper-type-${idx}`}>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-900 border-green-500">
                              {productTypes.length > 0
                                ? productTypes.map(pt => (
                                    <SelectItem key={pt.id} value={pt.name} className="text-white hover:bg-green-500/20">
                                      {pt.name}
                                    </SelectItem>
                                  ))
                                : ['Plain Kraft', 'Plain Duplex', 'Plain Gyt', 'Plain Imported', 'Printed Kraft', 'Printed Duplex', 'Printed Gyt', 'Printed Imported'].map(type => (
                                    <SelectItem key={type} value={type} className="text-white hover:bg-green-500/20">
                                      {type}
                                    </SelectItem>
                                  ))
                              }
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-gray-400 text-xs">Received Qty *</Label>
                          <Input type="number" value={entry.qty} onChange={(e) => updateReceivedEntry(idx, "qty", e.target.value)} placeholder="Quantity" className="bg-black/50 border-green-500/30 text-white mt-1" data-testid={`dialog-qty-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-gray-400 text-xs">RM Name</Label>
                          <Input value={entry.rmName} onChange={(e) => updateReceivedEntry(idx, "rmName", e.target.value)} placeholder="Enter RM Name" className="bg-black/50 border-green-500/30 text-white mt-1" data-testid={`dialog-rm-${idx}`} />
                        </div>
                      </div>
                    )}
                    
                    {/* Corrugation DC: Detailed layout with Item Name & Weight */}
                    {selectedOutwardDC.dc_subtype === "corrugation" && (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-gray-400 text-xs">Product Type *</Label>
                            <Select
                              value={entry.paperType || ""}
                              onValueChange={(value) => updateReceivedEntry(idx, "paperType", value)}
                            >
                              <SelectTrigger className="bg-black/50 border-green-500/30 text-white mt-1" data-testid={`dialog-paper-type-${idx}`}>
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent className="bg-gray-900 border-green-500">
                                {productTypes.length > 0
                                  ? productTypes.map(pt => (
                                      <SelectItem key={pt.id} value={pt.name} className="text-white hover:bg-green-500/20">
                                        {pt.name}
                                      </SelectItem>
                                    ))
                                  : ['Plain Kraft', 'Plain Duplex', 'Plain Gyt', 'Plain Imported', 'Printed Kraft', 'Printed Duplex', 'Printed Gyt', 'Printed Imported'].map(type => (
                                      <SelectItem key={type} value={type} className="text-white hover:bg-green-500/20">
                                        {type}
                                      </SelectItem>
                                    ))
                                }
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-gray-400 text-xs">Item Name (from DC)</Label>
                            <Input 
                              value={entry.itemName} 
                              readOnly 
                              className="bg-black/30 border-gray-600 text-cyan-400 font-mono mt-1 cursor-not-allowed" 
                              data-testid={`dialog-item-name-${idx}`} 
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-gray-400 text-xs">Received Qty *</Label>
                            <Input type="number" value={entry.qty} onChange={(e) => updateReceivedEntry(idx, "qty", e.target.value)} placeholder="Quantity" className="bg-black/50 border-green-500/30 text-white mt-1" data-testid={`dialog-qty-${idx}`} />
                          </div>
                          <div>
                            <Label className="text-gray-400 text-xs">RM Name</Label>
                            <Input value={entry.rmName} onChange={(e) => updateReceivedEntry(idx, "rmName", e.target.value)} placeholder="Enter RM Name" className="bg-black/50 border-green-500/30 text-white mt-1" data-testid={`dialog-rm-${idx}`} />
                          </div>
                          <div>
                            <Label className="text-gray-400 text-xs">Total Roll Weight (Kg)</Label>
                            <Input type="number" value={entry.totalRollWeight} onChange={(e) => updateReceivedEntry(idx, "totalRollWeight", e.target.value)} placeholder="Weight in Kg" className="bg-black/50 border-green-500/30 text-white mt-1" data-testid={`dialog-weight-${idx}`} />
                          </div>
                        </div>
                      </>
                    )}
                    
                    {/* Sample or other DC types: Use printing layout */}
                    {selectedOutwardDC.dc_subtype !== "printing" && selectedOutwardDC.dc_subtype !== "corrugation" && (
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-gray-400 text-xs">Paper Type *</Label>
                          <Select
                            value={entry.paperType || ""}
                            onValueChange={(value) => updateReceivedEntry(idx, "paperType", value)}
                          >
                            <SelectTrigger className="bg-black/50 border-green-500/30 text-white mt-1" data-testid={`dialog-paper-type-${idx}`}>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-900 border-green-500">
                              {productTypes.length > 0
                                ? productTypes.map(pt => (
                                    <SelectItem key={pt.id} value={pt.name} className="text-white hover:bg-green-500/20">
                                      {pt.name}
                                    </SelectItem>
                                  ))
                                : ['Plain Kraft', 'Plain Duplex', 'Plain Gyt', 'Plain Imported', 'Printed Kraft', 'Printed Duplex', 'Printed Gyt', 'Printed Imported'].map(type => (
                                    <SelectItem key={type} value={type} className="text-white hover:bg-green-500/20">
                                      {type}
                                    </SelectItem>
                                  ))
                              }
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-gray-400 text-xs">Received Qty *</Label>
                          <Input type="number" value={entry.qty} onChange={(e) => updateReceivedEntry(idx, "qty", e.target.value)} placeholder="Quantity" className="bg-black/50 border-green-500/30 text-white mt-1" data-testid={`dialog-qty-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-gray-400 text-xs">RM Name</Label>
                          <Input value={entry.rmName} onChange={(e) => updateReceivedEntry(idx, "rmName", e.target.value)} placeholder="Enter RM Name" className="bg-black/50 border-green-500/30 text-white mt-1" data-testid={`dialog-rm-${idx}`} />
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Total + Loss Trend */}
                {receivedEntries.some(e => e.qty) && (
                  <>
                    <div className="p-3 bg-black/30 rounded-lg flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Total Received:</span>
                      <span className="text-white font-bold">{receivedEntries.reduce((s, e) => s + (parseFloat(e.qty) || 0), 0)} {selectedOutwardDC.sent_unit || "Nos"}</span>
                    </div>
                    {selectedOutwardDC.dc_subtype === "printing" && (() => {
                      const sent = parseFloat(selectedOutwardDC.sent_qty) || 0;
                      const received = receivedEntries.reduce((s, e) => s + (parseFloat(e.qty) || 0), 0);
                      const { loss, pct, isLoss } = calculateLoss(sent, received);
                      return (
                        <div className="p-3 bg-gray-800 rounded-lg">
                          <p className="text-gray-400 text-sm mb-2">Loss Trend</p>
                          <div className="flex items-center gap-4">
                            <div className="flex-1 text-center"><p className="text-xs text-gray-500">Sent</p><p className="text-blue-400 font-bold text-lg">{sent}</p></div>
                            <div className="text-gray-500">&rarr;</div>
                            <div className="flex-1 text-center"><p className="text-xs text-gray-500">Received</p><p className="text-green-400 font-bold text-lg">{received}</p></div>
                            <div className="text-gray-500">=</div>
                            <div className="flex-1 text-center"><p className="text-xs text-gray-500">Loss</p>
                              {isLoss ? <span className="text-red-400 font-bold">{loss} ({pct}%)</span> : <span className="text-gray-400 font-bold">0</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowInwardDialog(false); setSelectedOutwardDC(null); setReceivedEntries([{ qty: "", rmName: "", remarks: "", paperType: "" }]); }}>
              Cancel
            </Button>
            <Button onClick={handleReceiveInward} className="bg-green-500 hover:bg-green-600 text-white font-bold">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Confirm Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View DC Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="bg-gray-900 border-cyan-500/50 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 text-xl">View DC: {viewDC?.dc_number}</DialogTitle>
          </DialogHeader>
          
          {viewDC && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-400">DC Number</p>
                  <p className="text-white font-bold">{viewDC.dc_number}</p>
                </div>
                <div>
                  <p className="text-gray-400">Type</p>
                  <p className="text-white font-bold capitalize">{viewDC.dc_subtype}</p>
                </div>
                <div>
                  <p className="text-gray-400">Vendor</p>
                  <p className="text-white">{viewDC.vendor_name}</p>
                </div>
                <div>
                  <p className="text-gray-400">Status</p>
                  <Badge variant="outline" className={viewDC.status === "received" ? "bg-green-500/20 text-green-400" : "bg-amber-500/20 text-amber-400"}>
                    {viewDC.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-gray-400">Sent Qty</p>
                  <p className="text-blue-400 font-bold">{viewDC.sent_qty} {viewDC.sent_unit || "Nos"}</p>
                </div>
                {viewDC.status === "received" && (
                  <div>
                    <p className="text-gray-400">Received Qty</p>
                    <p className="text-green-400 font-bold">{viewDC.received_qty}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-400">Created</p>
                  <p className="text-white">{new Date(viewDC.created_at).toLocaleString()}</p>
                </div>
                {viewDC.vehicle_no && (
                  <div>
                    <p className="text-gray-400">Vehicle</p>
                    <p className="text-white">{viewDC.vehicle_no}</p>
                  </div>
                )}
              </div>
              
              {viewDC.items?.length > 0 && (
                <div className="p-3 bg-gray-800 rounded">
                  <p className="text-gray-400 text-sm mb-2">Items</p>
                  {viewDC.items.map((item, i) => (
                    <div key={i} className="text-sm text-white">
                      {item.reel_id && <span className="text-cyan-400">{item.reel_id}</span>}
                      {item.rm_name && <span className="ml-2">{item.rm_name}</span>}
                      {item.description && <span>{item.description}</span>}
                      <span className="ml-2">- {item.qty} {item.unit}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {viewDC.remarks && (
                <div>
                  <p className="text-gray-400 text-sm">Remarks</p>
                  <p className="text-white">{viewDC.remarks}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
            <Button onClick={() => { setPrintDC(viewDC); setShowViewDialog(false); }} className="bg-blue-500">
              <Printer className="h-4 w-4 mr-2" /> Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Preview */}
      {printDC && (
        <Dialog open={!!printDC} onOpenChange={() => setPrintDC(null)}>
          <DialogContent className="bg-white text-black max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Print: {printDC.dc_number}</DialogTitle>
            </DialogHeader>
            
            <div className="p-6 bg-white">
              <div className="border-2 border-black p-6">
                <div className="text-center border-b-2 border-black pb-4 mb-4">
                  <h1 className="text-2xl font-bold">DELIVERY CHALLAN</h1>
                  <p className="text-sm text-gray-600">CPI Manufacturing</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  <div><strong>DC No:</strong> {printDC.dc_number}</div>
                  <div><strong>Date:</strong> {new Date(printDC.created_at).toLocaleDateString()}</div>
                  <div><strong>Type:</strong> {printDC.dc_subtype}</div>
                  {printDC.vehicle_no && <div><strong>Vehicle:</strong> {printDC.vehicle_no}</div>}
                </div>
                
                {/* Always show From and To Address */}
                <div className="grid grid-cols-2 gap-4 mb-4 border border-gray-300 rounded">
                  <div className="p-3 border-r border-gray-300">
                    <p className="font-bold text-sm mb-1">FROM:</p>
                    <p className="text-sm whitespace-pre-line">{printDC.from_address || "Challenge Packaging Industries\nSfno : 332/1A, Meetukuppam Main Road,\nVanagaram, Chennai - 600 095\nGSTIN - 33AGSPV7578C2ZP"}</p>
                  </div>
                  <div className="p-3">
                    <p className="font-bold text-sm mb-1">TO:</p>
                    <p className="text-sm whitespace-pre-line">{printDC.to_address || printDC.vendor_name || printDC.customer_name || "N/A"}</p>
                  </div>
                </div>
                
                <table className="w-full border-collapse border border-black mb-4 text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-black p-2">S.No</th>
                      <th className="border border-black p-2">Description</th>
                      {(printDC.dc_subtype === "corrugation") && <th className="border border-black p-2">Weight (Kg)</th>}
                      <th className="border border-black p-2">Qty</th>
                      <th className="border border-black p-2">Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {printDC.items?.map((item, i) => (
                      <tr key={i}>
                        <td className="border border-black p-2">{i + 1}</td>
                        <td className="border border-black p-2">{item.reel_id || item.description || "N/A"} {item.rm_name && `- ${item.rm_name}`}</td>
                        {(printDC.dc_subtype === "corrugation") && <td className="border border-black p-2 text-right">{item.weight || item.reel_weight || "-"}</td>}
                        <td className="border border-black p-2 text-right">{item.qty}</td>
                        <td className="border border-black p-2">{item.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {printDC.remarks && <p className="mb-4"><strong>Remarks:</strong> {printDC.remarks}</p>}
                
                <div className="grid grid-cols-3 gap-4 mt-8 pt-4 border-t">
                  <div className="text-center"><div className="h-16 border-b border-black mb-2"></div><p className="text-sm">Prepared By</p></div>
                  <div className="text-center"><div className="h-16 border-b border-black mb-2"></div><p className="text-sm">Checked By</p></div>
                  <div className="text-center"><div className="h-16 border-b border-black mb-2"></div><p className="text-sm">Received By</p></div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setPrintDC(null)}>Close</Button>
              <Button onClick={handlePrint} className="bg-blue-500 text-white"><Printer className="h-4 w-4 mr-2" /> Print</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Hidden Print Content for DC */}
      {printDC && (
        <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
          <div ref={printRef} className="p-6 bg-white text-black">
            <div className="border-2 border-black p-6">
              <div className="text-center border-b-2 border-black pb-4 mb-4">
                <h1 className="text-2xl font-bold">DELIVERY CHALLAN</h1>
                <p className="text-sm text-gray-600">CPI Manufacturing</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div><strong>DC No:</strong> {printDC.dc_number}</div>
                <div><strong>Date:</strong> {new Date(printDC.created_at).toLocaleDateString()}</div>
                <div><strong>Type:</strong> {printDC.dc_subtype}</div>
                {printDC.vehicle_no && <div><strong>Vehicle:</strong> {printDC.vehicle_no}</div>}
              </div>
              
              {/* Always show From/To Address block for all DC types */}
              <div className="grid grid-cols-2 gap-4 mb-4 border border-gray-300 rounded">
                <div className="p-3 border-r border-gray-300">
                  <p className="font-bold text-sm mb-1">FROM:</p>
                  <p className="text-sm whitespace-pre-line">{printDC.from_address || "Challenge Packaging Industries\nSfno : 332/1A, Meetukuppam Main Road,\nVanagaram, Chennai - 600 095\nGSTIN - 33AGSPV7578C2ZP"}</p>
                </div>
                <div className="p-3">
                  <p className="font-bold text-sm mb-1">TO:</p>
                  <p className="text-sm whitespace-pre-line">{printDC.to_address || printDC.vendor_name || printDC.customer_name || "N/A"}</p>
                </div>
              </div>
              
              <table className="w-full border-collapse border border-black mb-4 text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-black p-2">S.No</th>
                    <th className="border border-black p-2">Description</th>
                    {(printDC.dc_subtype === "corrugation") && <th className="border border-black p-2">Weight (Kg)</th>}
                    <th className="border border-black p-2">Qty</th>
                    <th className="border border-black p-2">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {printDC.items?.map((item, i) => (
                    <tr key={i}>
                      <td className="border border-black p-2">{i + 1}</td>
                      <td className="border border-black p-2">{item.reel_id || item.description || "N/A"} {item.rm_name && `- ${item.rm_name}`}</td>
                      {(printDC.dc_subtype === "corrugation") && <td className="border border-black p-2 text-right">{item.reel_weight || item.weight || "-"}</td>}
                      <td className="border border-black p-2 text-right">{item.qty}</td>
                      <td className="border border-black p-2">{item.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {printDC.remarks && <p className="mb-4"><strong>Remarks:</strong> {printDC.remarks}</p>}
              
              <div className="grid grid-cols-3 gap-4 mt-8 pt-4 border-t">
                <div className="text-center"><div className="h-16 border-b border-black mb-2"></div><p className="text-sm">Prepared By</p></div>
                <div className="text-center"><div className="h-16 border-b border-black mb-2"></div><p className="text-sm">Checked By</p></div>
                <div className="text-center"><div className="h-16 border-b border-black mb-2"></div><p className="text-sm">Received By</p></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden Printable Loss Report */}
      <div style={{ display: "none" }}>
        <div ref={lossReportRef} className="p-8 bg-white text-black">
          <div className="border-2 border-black p-6">
            <div className="text-center border-b-2 border-black pb-4 mb-4">
              <h1 className="text-2xl font-bold">DELIVERY CHALLAN - LOSS REPORT</h1>
              <p className="text-sm text-gray-600">CPI Manufacturing</p>
              <p className="text-sm mt-1">
                {vendorFilter === "all" ? "All Vendors" : `Vendor: ${vendorFilter}`}
              </p>
              <p className="text-xs text-gray-500">Generated: {new Date().toLocaleString()}</p>
            </div>
            
            <table className="w-full border-collapse border border-black mb-4 text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-black p-2 text-left">DC No</th>
                  <th className="border border-black p-2 text-left">Type</th>
                  <th className="border border-black p-2 text-left">Vendor</th>
                  <th className="border border-black p-2 text-left">Reel ID</th>
                  <th className="border border-black p-2 text-right">Sent Qty</th>
                  <th className="border border-black p-2 text-right">Received</th>
                  <th className="border border-black p-2 text-right">Loss</th>
                  <th className="border border-black p-2 text-right">Loss %</th>
                  <th className="border border-black p-2 text-left">Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredChallans.map((dc) => {
                  const sentQty = dc.sent_qty || dc.items?.[0]?.qty || 0;
                  const receivedQty = dc.received_qty || 0;
                  const { loss, pct, isLoss } = calculateLoss(sentQty, receivedQty);
                  
                  return (
                    <tr key={dc.dc_number}>
                      <td className="border border-black p-2 font-bold">{dc.dc_number}</td>
                      <td className="border border-black p-2">{dc.dc_subtype || "N/A"}</td>
                      <td className="border border-black p-2">{dc.vendor_name}</td>
                      <td className="border border-black p-2">{dc.items?.[0]?.reel_id || "N/A"}</td>
                      <td className="border border-black p-2 text-right">{sentQty}</td>
                      <td className="border border-black p-2 text-right">{dc.status === "received" ? receivedQty : "-"}</td>
                      <td className="border border-black p-2 text-right" style={{ color: isLoss ? "red" : "green" }}>
                        {dc.status === "received" ? (isLoss ? `-${loss}` : loss < 0 ? `+${Math.abs(loss)}` : "0") : "-"}
                      </td>
                      <td className="border border-black p-2 text-right">
                        {dc.status === "received" ? `${pct}%` : "-"}
                      </td>
                      <td className="border border-black p-2">{new Date(dc.created_at).toLocaleDateString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {/* Summary */}
            <div className="border border-black p-4 mt-4">
              <h3 className="font-bold mb-2">SUMMARY</h3>
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Total DCs:</p>
                  <p className="font-bold">{filteredChallans.length}</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Sent:</p>
                  <p className="font-bold">
                    {filteredChallans.reduce((sum, dc) => sum + (dc.sent_qty || dc.items?.[0]?.qty || 0), 0)} Nos
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Total Received:</p>
                  <p className="font-bold">
                    {filteredChallans.filter(dc => dc.status === "received").reduce((sum, dc) => sum + (dc.received_qty || 0), 0)} Nos
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Total Loss:</p>
                  <p className="font-bold" style={{ color: "red" }}>
                    {filteredChallans.filter(dc => dc.status === "received").reduce((sum, dc) => {
                      const sent = dc.sent_qty || dc.items?.[0]?.qty || 0;
                      const received = dc.received_qty || 0;
                      return sum + Math.max(0, sent - received);
                    }, 0)} Nos
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-8 pt-4 border-t">
              <div className="text-center"><div className="h-16 border-b border-black mb-2"></div><p className="text-sm">Verified By</p></div>
              <div className="text-center"><div className="h-16 border-b border-black mb-2"></div><p className="text-sm">Approved By</p></div>
            </div>
          </div>
        </div>
      </div>
      {/* Inward Select Dialog - shown when clicking "Receive Inward" from top bar */}
      <Dialog open={showInwardSelectDialog} onOpenChange={setShowInwardSelectDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-green-400">Select Pending DC to Receive</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {pendingOutwardDCs.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Clock className="h-8 w-8 mx-auto mb-2 text-gray-600" />
                <p>No pending outward DCs available</p>
              </div>
            ) : (
              pendingOutwardDCs.map(dc => (
                <div
                  key={dc.dc_number}
                  onClick={() => {
                    setShowInwardSelectDialog(false);
                    openReceiveDialog(dc);
                  }}
                  className="p-3 bg-black/30 border border-gray-700 rounded cursor-pointer hover:border-green-500/50 transition-all"
                  data-testid={`select-pending-dc-${dc.dc_number}`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-cyan-400">{dc.dc_number}</span>
                    <Badge variant="outline" className={
                      dc.dc_subtype === "printing" ? "border-purple-500/50 text-purple-400" :
                      dc.dc_subtype === "corrugation" ? "border-orange-500/50 text-orange-400" :
                      "border-gray-500/50 text-gray-400"
                    }>
                      {dc.dc_subtype}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-400 mt-1">
                    {dc.vendor_name} | Sent: {dc.sent_qty} {dc.sent_unit || "Nos"}
                  </p>
                  {dc.items?.map((item, i) => (
                    <p key={i} className="text-xs text-gray-500 mt-0.5">
                      {item.reel_id || item.rm_name} - {item.paper_count || item.quantity} {dc.sent_unit || "Nos"}
                    </p>
                  ))}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryChallan;
